import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  ShieldCheck, HeartHandshake, Sparkles, Eye, Users, TrendingUp, Sprout, HandHeart,
} from "lucide-react";
import { Reveal, Eyebrow } from "../components/primitives";
import { Seo } from "../components/Seo";
import { api } from "../lib/api";

const ICONS = {
  "shield-check": ShieldCheck,
  "heart-handshake": HeartHandshake,
  sparkles: Sparkles,
  eye: Eye,
  users: Users,
  "trending-up": TrendingUp,
  sprout: Sprout,
  "hand-heart": HandHeart,
};

const EASE = [0.22, 1, 0.36, 1];
const cardInitial = { opacity: 0, y: 30 };
const cardInView = { opacity: 1, y: 0 };
const cardViewport = { once: true, margin: "-60px" };
const cardHover = { y: -6 };

export default function Values() {
  const { data: values = [] } = useQuery({
    queryKey: ["values"],
    queryFn: async () => (await api.get("/values")).data,
  });

  return (
    <>
      <Seo title="Our Values" description="Integrity, compassion, excellence, transparency, community, growth, stewardship and human dignity — the values Glam Wagon is built on." />
      <section className="pt-40 pb-16 max-w-7xl mx-auto px-6 lg:px-12" data-testid="values-hero">
        <Reveal>
          <Eyebrow>What we stand for</Eyebrow>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-[5rem] leading-[0.98] tracking-tight mt-6 max-w-4xl text-ink">
            Eight values. One standard.
          </h1>
          <p className="mt-8 text-lg leading-relaxed text-ink-soft max-w-2xl">
            These aren't aspirations on a wall. They are the criteria we hold every decision against.
          </p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-28">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="values-grid">
          {values.map((v, i) => {
            const Icon = ICONS[v.icon] || Sparkles;
            return (
              <motion.div
                key={v.name}
                data-testid={`value-card-${v.name.toLowerCase().replace(/\s/g, "-")}`}
                initial={cardInitial}
                whileInView={cardInView}
                viewport={cardViewport}
                transition={{ duration: 0.6, delay: (i % 4) * 0.08, ease: EASE }}
                whileHover={cardHover}
                className="group rounded-2xl border border-line bg-surface p-8 hover:shadow-xl hover:shadow-black/5 transition-shadow duration-500"
              >
                <div className="w-12 h-12 rounded-full bg-cream border border-line flex items-center justify-center text-gold group-hover:bg-gold group-hover:text-white transition-colors duration-500">
                  <Icon size={22} />
                </div>
                <h3 className="font-serif text-2xl mt-6 text-ink">{v.name}</h3>
                <p className="mt-3 text-ink-soft leading-relaxed text-[0.95rem]">{v.description}</p>
              </motion.div>
            );
          })}
        </div>
      </section>
    </>
  );
}
